import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import '../styles/MetricInfoModal.css';

const MetricInfoModal = ({ metricKey, entityType, scope, apiUrl, onClose }) => {
    const [definition, setDefinition] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        if (!metricKey) return;

        const fetchDefinition = async () => {
            setLoading(true);
            setError(null);

            try {
                const res = await fetch(
                    `${apiUrl}/metrics/definition?metricKey=${metricKey}&entityType=${entityType}&scope=${scope}`
                );

                if (res.status === 202) {
                    const retryAfter = parseInt(res.headers.get('Retry-After') || '5');
                    setTimeout(() => {
                        setRetryCount(prev => prev + 1);
                    }, retryAfter * 1000);
                    return;
                }

                if (!res.ok) {
                    throw new Error('Failed to load metric definition');
                }

                const data = await res.json();
                setDefinition(data.data);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching definition:', err);
                setError(err.message);
                setLoading(false);
            }
        };

        fetchDefinition();
    }, [metricKey, entityType, scope, apiUrl, retryCount]);

    if (!metricKey) return null;

    return (
        <div className="metric-info-overlay" onClick={onClose}>
            <div className="metric-info-modal" onClick={e => e.stopPropagation()}>
                <button className="metric-info-close" onClick={onClose}>×</button>

                {loading && (
                    <div className="metric-info-loading">
                        <div className="spinner"></div>
                        <p>Generating comprehensive metric definition...</p>
                        {retryCount > 0 && <p className="retry-notice">Using latest AI model (attempt {retryCount})...</p>}
                    </div>
                )}

                {error && (
                    <div className="metric-info-error">
                        <p>Error: {error}</p>
                        <button onClick={() => window.location.reload()}>Retry</button>
                    </div>
                )}

                {definition && (
                    <div className="metric-info-content">
                        {/* Header */}
                        <div className="metric-info-header">
                            <h2>{definition.title}</h2>
                            <p className="metric-overview">{definition.overview}</p>
                            <div className="metric-meta">
                                <span className="meta-tag">{definition.entityType}</span>
                                <span className="meta-tag">{definition.scope}</span>
                            </div>
                        </div>

                        {/* Metric Snapshot */}
                        {definition.metricSnapshot && (
                            <div className="metric-section snapshot-section">
                                <h3>📊 Metric Snapshot</h3>
                                <p><strong>What it measures:</strong> {definition.metricSnapshot.whatItMeasures}</p>
                                <p><strong>Interpretation:</strong> {definition.metricSnapshot.typicalInterpretation}</p>
                            </div>
                        )}

                        {/* Why It Matters */}
                        {definition.whyItMatters && (
                            <div className="metric-section why-matters-section">
                                <h3>💡 Why It Matters</h3>
                                <p className="decision-relevance">{definition.whyItMatters.decisionRelevance}</p>
                                {definition.whyItMatters.investorUseCases && definition.whyItMatters.investorUseCases.length > 0 && (
                                    <div className="use-cases">
                                        <h4>Investor Use Cases:</h4>
                                        <ul>
                                            {definition.whyItMatters.investorUseCases.map((useCase, idx) => (
                                                <li key={idx}>{useCase}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Formula */}
                        {definition.formula && (
                            <div className="metric-section formula-section">
                                <h3>📐 Formula & Calculation</h3>
                                <div className="formula-display">
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm, remarkMath]}
                                        rehypePlugins={[rehypeKatex]}
                                    >
                                        {`$$${definition.formula.latex}$$`}
                                    </ReactMarkdown>
                                    <p className="formula-plain">{definition.formula.plain}</p>
                                </div>
                                {definition.formula.inputs && definition.formula.inputs.length > 0 && (
                                    <div className="formula-inputs">
                                        <h4>Inputs:</h4>
                                        <ul>
                                            {definition.formula.inputs.map((input, idx) => (
                                                <li key={idx}>
                                                    <strong>{input.name}</strong> ({input.sourceStatement})
                                                    {input.finlyticsNotes && <div className="input-note">{input.finlyticsNotes}</div>}
                                                    {input.signConvention && <div className="sign-note"><em>Sign: {input.signConvention}</em></div>}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {definition.formula.calculationNotes && (
                                    <div className="calculation-notes">
                                        <p><strong>Note:</strong> {definition.formula.calculationNotes}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Benchmarks */}
                        {definition.benchmarks && (
                            <div className="metric-section benchmarks-section">
                                <h3>🎯 Benchmarks & Thresholds</h3>
                                {definition.benchmarks.interpretation && (
                                    <p className="benchmark-interpretation">{definition.benchmarks.interpretation}</p>
                                )}
                                <div className="benchmark-grid">
                                    {definition.benchmarks.greenThreshold && (
                                        <div className="benchmark-item green-item">
                                            <span className="benchmark-label">✅ Ideal Range:</span>
                                            <span className="benchmark-value">{definition.benchmarks.greenThreshold}</span>
                                        </div>
                                    )}
                                    {definition.benchmarks.yellowThreshold && (
                                        <div className="benchmark-item yellow-item">
                                            <span className="benchmark-label">⚠️ Watch Range:</span>
                                            <span className="benchmark-value">{definition.benchmarks.yellowThreshold}</span>
                                        </div>
                                    )}
                                    {definition.benchmarks.redThreshold && (
                                        <div className="benchmark-item red-item">
                                            <span className="benchmark-label">❌ Concern Range:</span>
                                            <span className="benchmark-value">{definition.benchmarks.redThreshold}</span>
                                        </div>
                                    )}
                                </div>
                                {definition.benchmarks.industryVariations && definition.benchmarks.industryVariations.length > 0 && (
                                    <div className="industry-variations">
                                        <h4>Industry-Specific Benchmarks:</h4>
                                        <ul>
                                            {definition.benchmarks.industryVariations.map((variation, idx) => (
                                                <li key={idx}>{variation}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {definition.benchmarks.caveats && definition.benchmarks.caveats.length > 0 && (
                                    <div className="benchmark-caveats">
                                        <h4>Caveats:</h4>
                                        <ul>
                                            {definition.benchmarks.caveats.map((caveat, idx) => (
                                                <li key={idx}>{caveat}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Trend Guidance */}
                        {definition.trendGuidance && (
                            <div className="metric-section trend-section">
                                <h3>📈 Trend Interpretation</h3>
                                <p><strong>Ideal Pattern:</strong> {definition.trendGuidance.idealPattern}</p>
                                {definition.trendGuidance.redFlags && definition.trendGuidance.redFlags.length > 0 && (
                                    <div className="trend-red-flags">
                                        <h4>Red Flags to Watch:</h4>
                                        <ul>
                                            {definition.trendGuidance.redFlags.map((flag, idx) => (
                                                <li key={idx}>{flag}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {definition.trendGuidance.investigationTriggers && (
                                    <p className="investigation-note"><strong>Investigation Triggers:</strong> {definition.trendGuidance.investigationTriggers}</p>
                                )}
                            </div>
                        )}

                        {/* Drivers */}
                        {definition.drivers && (
                            <div className="metric-section drivers-section">
                                <h3>🔧 What Drives This Metric</h3>
                                {definition.drivers.businessLevers && definition.drivers.businessLevers.length > 0 && (
                                    <div className="driver-group">
                                        <h4>Business Levers:</h4>
                                        <ul>
                                            {definition.drivers.businessLevers.map((lever, idx) => (
                                                <li key={idx}>{lever}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {definition.drivers.accountingLevers && definition.drivers.accountingLevers.length > 0 && (
                                    <div className="driver-group">
                                        <h4>Accounting Levers:</h4>
                                        <ul>
                                            {definition.drivers.accountingLevers.map((lever, idx) => (
                                                <li key={idx}>{lever}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {definition.drivers.externalFactors && definition.drivers.externalFactors.length > 0 && (
                                    <div className="driver-group">
                                        <h4>External Factors:</h4>
                                        <ul>
                                            {definition.drivers.externalFactors.map((factor, idx) => (
                                                <li key={idx}>{factor}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Dependencies */}
                        {definition.dependencies && (
                            <div className="metric-section dependencies-section">
                                <h3>🔗 Metric Dependencies</h3>
                                {definition.dependencies.upstreamDrivers && definition.dependencies.upstreamDrivers.length > 0 && (
                                    <div className="dependency-group">
                                        <h4>Upstream Drivers (what feeds into this):</h4>
                                        <ul>
                                            {definition.dependencies.upstreamDrivers.map((driver, idx) => (
                                                <li key={idx}>{driver}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {definition.dependencies.downstreamImpacts && definition.dependencies.downstreamImpacts.length > 0 && (
                                    <div className="dependency-group">
                                        <h4>Downstream Impacts (what this affects):</h4>
                                        <ul>
                                            {definition.dependencies.downstreamImpacts.map((impact, idx) => (
                                                <li key={idx}>{impact}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Pitfalls */}
                        {definition.pitfalls && (
                            <div className="metric-section pitfalls-section">
                                <h3>⚠️ Pitfalls & Manipulation Tactics</h3>
                                {definition.pitfalls.manipulationTactics && definition.pitfalls.manipulationTactics.length > 0 && (
                                    <div className="pitfall-group">
                                        <h4>How Management Can Manipulate:</h4>
                                        <ul className="manipulation-list">
                                            {definition.pitfalls.manipulationTactics.map((tactic, idx) => (
                                                <li key={idx}>{tactic}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {definition.pitfalls.commonMisinterpretations && definition.pitfalls.commonMisinterpretations.length > 0 && (
                                    <div className="pitfall-group">
                                        <h4>Common Misinterpretations:</h4>
                                        <ul>
                                            {definition.pitfalls.commonMisinterpretations.map((mis, idx) => (
                                                <li key={idx}>{mis}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {definition.pitfalls.dataQualityIssues && definition.pitfalls.dataQualityIssues.length > 0 && (
                                    <div className="pitfall-group">
                                        <h4>Data Quality Issues:</h4>
                                        <ul>
                                            {definition.pitfalls.dataQualityIssues.map((issue, idx) => (
                                                <li key={idx}>{issue}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Cross-checks */}
                        {definition.crossChecks && (
                            <div className="metric-section crosschecks-section">
                                <h3>✅ Sanity Checks & Validation</h3>
                                {definition.crossChecks.sanityChecks && definition.crossChecks.sanityChecks.length > 0 && (
                                    <div className="crosscheck-group">
                                        <h4>How to Verify:</h4>
                                        <ul>
                                            {definition.crossChecks.sanityChecks.map((check, idx) => (
                                                <li key={idx}>{check}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {definition.crossChecks.relatedMetrics && definition.crossChecks.relatedMetrics.length > 0 && (
                                    <div className="crosscheck-group">
                                        <h4>Compare With:</h4>
                                        <ul>
                                            {definition.crossChecks.relatedMetrics.map((metric, idx) => (
                                                <li key={idx}>{metric}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Investor Takeaways */}
                        {definition.investorTakeaways && definition.investorTakeaways.length > 0 && (
                            <div className="metric-section takeaways-section">
                                <h3>🎯 Key Investor Takeaways</h3>
                                <ul className="takeaway-list">
                                    {definition.investorTakeaways.map((takeaway, idx) => (
                                        <li key={idx}>{takeaway}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Examples */}
                        {definition.examples && definition.examples.length > 0 && (
                            <div className="metric-section examples-section">
                                <h3>💡 Real-World Examples</h3>
                                {definition.examples.map((example, idx) => (
                                    <div key={idx} className="example-card">
                                        <h4>{example.scenario}</h4>
                                        <p className="example-description">{example.description}</p>
                                        <p className="example-lesson"><strong>Lesson:</strong> {example.lesson}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Sources */}
                        {definition.sources && (
                            <div className="metric-section sources-section">
                                <h3>📚 Sources & Standards</h3>
                                {definition.sources.finlyticsStandard && (
                                    <p><strong>Finlytics Standard:</strong> {definition.sources.finlyticsStandard}</p>
                                )}
                                {definition.sources.industryReferences && definition.sources.industryReferences.length > 0 && (
                                    <div>
                                        <h4>References:</h4>
                                        <ul>
                                            {definition.sources.industryReferences.map((ref, idx) => (
                                                <li key={idx}>{ref}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {definition.sources.consolidatedVsStandalone && (
                                    <p className="scope-note"><strong>Consolidated vs Standalone:</strong> {definition.sources.consolidatedVsStandalone}</p>
                                )}
                            </div>
                        )}

                        {/* Footer */}
                        <div className="metric-info-footer">
                            <p className="generation-info">Generated by {definition.model} • {new Date(definition.generatedAt).toLocaleString()}</p>
                            <div className="metric-tags">
                                {definition.tags && definition.tags.map((tag, idx) => (
                                    <span key={idx} className="metric-tag">{tag}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MetricInfoModal;
